import React from 'react';
import PropTypes from 'prop-types';
import { RowerCard } from './';
import { crewsAPI } from '../../services/api';
import { useNotifications } from '../notifications';

const UnassignedMembers = ({ selectedCrew, onDragStart, onCrewUpdate }) => {
  const { showToast, showConfirm } = useNotifications();

  if (!selectedCrew || !selectedCrew.members) {
    return null;
  }

  if (selectedCrew.is_experimental) {
    return null;
  }

  const unassignedMembers = selectedCrew.members.filter((m) => m.seat_number === null);

  const handleRemoveMember = async (memberId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to remove this member from the crew?',
      'Remove Member'
    );

    if (!confirmed) {
      return;
    }

    try {
      await crewsAPI.removeCrewMember(selectedCrew.id, memberId);
      showToast('Member removed successfully', 'success');
      // Trigger crew data refresh
      if (onCrewUpdate) {
        onCrewUpdate();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showToast('Failed to remove member', 'error');
    }
  };

  const handleClearAllUnassigned = async () => {
    const confirmed = await showConfirm(
      `Are you sure you want to remove all ${unassignedMembers.length} unassigned members from the crew?`,
      'Remove All Unassigned Members'
    );

    if (!confirmed) {
      return;
    }

    try {
      // Remove all unassigned members sequentially
      for (const member of unassignedMembers) {
        await crewsAPI.removeCrewMember(selectedCrew.id, member.id);
      }
      showToast(`Removed ${unassignedMembers.length} unassigned members`, 'success');
      // Trigger crew data refresh
      if (onCrewUpdate) {
        onCrewUpdate();
      }
    } catch (error) {
      console.error('Error removing members:', error);
      showToast('Failed to remove some members', 'error');
    }
  };

  if (unassignedMembers.length === 0) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center">
          <div className="mr-2 h-3 w-3 rounded-full bg-yellow-400"></div>
          <h4 className="text-lg font-semibold text-gray-800">
            Unassigned Members ({unassignedMembers.length})
          </h4>
        </div>
        <button
          onClick={handleClearAllUnassigned}
          className="rounded-md bg-red-500 px-3 py-1 text-sm text-white transition-colors hover:bg-red-600"
          title="Remove all unassigned members"
        >
          Clear All
        </button>
      </div>
      <p className="mb-3 text-sm text-gray-600">
        These crew members need to be assigned to seats. Drag them to position slots.
      </p>
      <div className="flex flex-wrap gap-2">
        {unassignedMembers.map((member) => (
          <div key={member.id} className="group relative">
            <RowerCard rower={member.user} isAssigned={false} onDragStart={onDragStart} />
            <button
              onClick={() => handleRemoveMember(member.id)}
              className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-sm font-bold text-white opacity-0 shadow-md transition-colors hover:bg-red-600 group-hover:opacity-100"
              title="Remove from crew"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

UnassignedMembers.propTypes = {
  selectedCrew: PropTypes.object,
  onDragStart: PropTypes.func.isRequired,
  onCrewUpdate: PropTypes.func,
};

export default UnassignedMembers;
